import { getAiInferencePresignedUrl } from '../../api/aiInference';
import { getRenderHistory } from '../../api/rooms';
import { resolveRoomObjectImageSrcForDisplay } from '../roomObjectImage';
import { normalizeRenderResultS3KeyRef } from './renderHistoryDisplay';

export { SAVED_FLYTHROUGH_HISTORY_LIMIT } from './flythroughVideoConfig';
export { SERVER_RENDER_HISTORY_SAVE_LIMIT as SERVER_FLYTHROUGH_HISTORY_SAVE_LIMIT } from './renderHistoryDisplay';

function isHttpUrl(value) {
  return typeof value === 'string' && /^https?:\/\//i.test(value.trim());
}

export function getFlythroughHistoryItemVideoS3Key(item) {
  const values = [
    item?.flythroughS3Key,
    item?.flythrough_s3_key,
    item?.flythrough?.s3Key,
    item?.flythrough?.s3_key,
    item?.resultVideoS3Key,
    item?.result_video_s3_key,
    item?.resultS3Key,
    item?.result_s3_key,
  ];
  for (const value of values) {
    if (typeof value === 'string' && value.trim() && !isHttpUrl(value)) {
      return value.trim();
    }
  }
  return '';
}

export function getFlythroughHistoryItemVideoKeyRef(item) {
  const values = [
    item?.flythroughS3Key,
    item?.flythrough_s3_key,
    item?.flythrough?.s3Key,
    item?.flythrough?.s3_key,
    item?.resultVideoS3Key,
    item?.result_video_s3_key,
    item?.resultS3Key,
    item?.result_s3_key,
  ];
  for (const value of values) {
    const normalized = normalizeRenderResultS3KeyRef(value);
    if (normalized) return normalized;
  }
  return '';
}

export function dedupeFlythroughHistoryById(items = []) {
  const seen = new Set();
  return items.filter((item) => {
    const id = item?.id;
    if (id == null) return true;
    const key = String(id);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function uniqueStrings(values = []) {
  return values.filter((value, index, arr) => {
    if (typeof value !== 'string') return false;
    const trimmed = value.trim();
    if (!trimmed) return false;
    return arr.findIndex((v) => typeof v === 'string' && v.trim() === trimmed) === index;
  });
}

/** Direct video URL candidates from a render-history item (API may embed presigned URLs in *S3Key fields). */
export function getFlythroughHistoryVideoUrlCandidates(item) {
  const values = [
    item?.videoUrl,
    item?.video_url,
    item?.flythroughUrl,
    item?.flythrough_url,
    item?.resultUrl,
    item?.result_url,
    item?.resultVideoUrl,
    item?.result_video_url,
    item?.flythroughS3Key,
    item?.flythrough_s3_key,
    item?.resultVideoS3Key,
    item?.result_video_s3_key,
    item?.resultS3Key,
    item?.result_s3_key,
  ];

  return uniqueStrings(values).filter(isHttpUrl);
}

export function getFlythroughHistoryThumbnailUrlCandidates(item) {
  const values = [
    item?.thumbnailUrl,
    item?.thumbnail_url,
    item?.imageUrl,
    item?.image_url,
    item?.thumbnailS3Key,
    item?.thumbnail_s3_key,
  ];

  return uniqueStrings(values).filter(isHttpUrl);
}

export function getFlythroughHistoryVideoS3KeyCandidates(item) {
  const values = [
    item?.flythroughS3Key,
    item?.flythrough_s3_key,
    item?.resultVideoS3Key,
    item?.result_video_s3_key,
    item?.resultS3Key,
    item?.result_s3_key,
  ];

  return uniqueStrings(values).filter((value) => !isHttpUrl(value));
}

const flythroughPlaybackUrlCache = new Map();

export function rememberFlythroughPlaybackUrl(s3Key, videoUrl) {
  const normalizedKey = normalizeRenderResultS3KeyRef(s3Key);
  const normalizedUrl =
    typeof videoUrl === 'string' ? resolveRoomObjectImageSrcForDisplay(videoUrl.trim()) : '';
  if (!normalizedKey || !normalizedUrl) return;
  flythroughPlaybackUrlCache.set(normalizedKey, normalizedUrl);
}

export function getRememberedFlythroughPlaybackUrl(s3Key) {
  const normalizedKey = normalizeRenderResultS3KeyRef(s3Key);
  if (!normalizedKey) return null;
  return flythroughPlaybackUrlCache.get(normalizedKey) ?? null;
}

export function getFlythroughHistoryItemThumbnailS3Key(item) {
  const values = [item?.thumbnailS3Key, item?.thumbnail_s3_key];
  for (const value of values) {
    if (typeof value === 'string' && value.trim() && !isHttpUrl(value)) {
      return value.trim();
    }
  }
  return '';
}

export function isFlythroughRenderHistoryEntry(item) {
  const renderType = String(item?.renderType ?? item?.render_type ?? '').toLowerCase();
  if (renderType === 'flythrough') return true;
  if (renderType === 'room_image') return false;

  const taskName = item?.config?.taskName ?? item?.config?.task_name;
  if (taskName === 'generate_flythrough') return true;
  if (taskName === 'room_image_render') return false;

  const flythroughKey = getFlythroughHistoryItemVideoS3Key(item);
  const flythroughUrl = getFlythroughHistoryVideoUrlCandidates(item)[0] ?? '';
  const looksLikeVideoRef = [flythroughKey, flythroughUrl].some(
    (value) => typeof value === 'string' && /\.(mp4|webm|mov)(\?|$)/i.test(value.trim()),
  );

  if (looksLikeVideoRef) return true;
  if (flythroughKey) return true;
  if (flythroughUrl) return true;

  return false;
}

export async function loadFlythroughRenderHistoryEntries(roomId, roomImageId) {
  if (roomId == null || roomImageId == null) return [];

  const typedList = await getRenderHistory(roomId, roomImageId, { renderType: 'flythrough' });
  const typedFiltered = filterFlythroughRenderHistoryEntries(typedList);
  if (typedFiltered.length > 0) {
    return typedFiltered;
  }

  const allList = await getRenderHistory(roomId, roomImageId);
  return filterFlythroughRenderHistoryEntries(allList);
}

export function filterFlythroughRenderHistoryEntries(items = []) {
  return items.filter(isFlythroughRenderHistoryEntry);
}

export function isFlythroughResultAlreadyInHistory(localItem, historyItems = []) {
  const localId = localItem?.id;
  if (localId != null) {
    const idKey = String(localId);
    if (historyItems.some((entry) => entry?.id != null && String(entry.id) === idKey)) {
      return true;
    }
  }

  const localKey = getFlythroughHistoryItemVideoKeyRef(localItem);
  if (!localKey) return false;
  return historyItems.some((entry) => {
    const historyKey = getFlythroughHistoryItemVideoKeyRef(entry);
    return historyKey && historyKey === localKey;
  });
}

/**
 * Flythrough worker stores a separate *_thumb.* object, but the presign API only
 * authorizes the primary result object (e.g. .mp4) on the AI task. Skip presign
 * for thumb keys and use the video URL as the display fallback instead.
 */
export function isFlythroughThumbnailPresignUnsupported(s3Key) {
  if (typeof s3Key !== 'string') return false;
  return /_thumb\.[a-z0-9]+$/i.test(s3Key.trim());
}

async function presignFlythroughS3Key(s3Key) {
  if (typeof s3Key !== 'string' || !s3Key.trim()) return null;
  if (isFlythroughThumbnailPresignUnsupported(s3Key)) return null;
  try {
    const presigned = await getAiInferencePresignedUrl(s3Key.trim());
    const rawUrl = presigned?.url ?? presigned?.presignedUrl;
    if (!rawUrl) return null;
    return resolveRoomObjectImageSrcForDisplay(String(rawUrl).trim());
  } catch {
    return null;
  }
}

export async function resolveFlythroughItemDisplayUrls(entry) {
  const videoS3Key = getFlythroughHistoryItemVideoS3Key(entry);
  const thumbnailS3Key = getFlythroughHistoryItemThumbnailS3Key(entry);

  let videoUrl = null;
  for (const direct of getFlythroughHistoryVideoUrlCandidates(entry)) {
    videoUrl = resolveRoomObjectImageSrcForDisplay(String(direct).trim());
    if (videoUrl) break;
  }

  if (!videoUrl) {
    for (const key of getFlythroughHistoryVideoS3KeyCandidates(entry)) {
      videoUrl = await presignFlythroughS3Key(key);
      if (videoUrl) break;
    }
  }

  if (!videoUrl) {
    const rememberedKeys = [videoS3Key, ...getFlythroughHistoryVideoS3KeyCandidates(entry)];
    for (const key of rememberedKeys) {
      const remembered = getRememberedFlythroughPlaybackUrl(key);
      if (remembered) {
        videoUrl = remembered;
        break;
      }
    }
  }

  if (videoUrl) {
    for (const key of [videoS3Key, ...getFlythroughHistoryVideoS3KeyCandidates(entry)]) {
      rememberFlythroughPlaybackUrl(key, videoUrl);
    }
  }

  let thumbnailUrl = null;
  for (const direct of getFlythroughHistoryThumbnailUrlCandidates(entry)) {
    if (isFlythroughThumbnailPresignUnsupported(direct)) continue;
    thumbnailUrl = resolveRoomObjectImageSrcForDisplay(String(direct).trim());
    if (thumbnailUrl) break;
  }

  if (!thumbnailUrl && thumbnailS3Key && !isFlythroughThumbnailPresignUnsupported(thumbnailS3Key)) {
    thumbnailUrl = await presignFlythroughS3Key(thumbnailS3Key);
  }

  return { videoS3Key, thumbnailS3Key, videoUrl, thumbnailUrl };
}

export async function enrichFlythroughRenderHistoryItems(items = []) {
  return Promise.all(
    items.map(async (entry) => {
      const { videoS3Key, thumbnailS3Key, videoUrl, thumbnailUrl } =
        await resolveFlythroughItemDisplayUrls(entry);

      return {
        ...entry,
        isLocal: false,
        resultVideoS3Key: videoS3Key || getFlythroughHistoryVideoS3KeyCandidates(entry)[0] || '',
        thumbnailS3Key,
        videoUrl,
        imageUrl: thumbnailUrl ?? null,
        lightboxImageUrl: thumbnailUrl ?? null,
        imageUrlCandidates: [thumbnailUrl, videoUrl].filter(Boolean),
      };
    }),
  );
}

export async function enrichLocalFlythroughResults(items = []) {
  return Promise.all(
    items.map(async (entry) => {
      const displayItem = {
        ...entry,
        isLocal: true,
        result_video_s3_key: entry.resultVideoS3Key,
        thumbnail_s3_key: entry.thumbnailS3Key,
        createdAt: entry.completedAt ?? entry.startedAt ?? entry.createdAt,
      };

      const { videoUrl, thumbnailUrl } = await resolveFlythroughItemDisplayUrls(displayItem);

      return {
        ...displayItem,
        videoUrl,
        imageUrl: thumbnailUrl ?? null,
        lightboxImageUrl: thumbnailUrl ?? null,
        imageUrlCandidates: [thumbnailUrl, videoUrl].filter(Boolean),
      };
    }),
  );
}
